import {
  AccountLevelsUsage, AccountType, CustomLevelUsage, NewUser, NotificationSetting, User, UserProfile,
  UserType
} from '../../models/user';
import {Injectable} from '@angular/core';
import {apiEndpoints, defaultPlatformSettings, CommonConstant} from '../app.constants';
import {Observable} from 'rxjs/Observable';
import {BackendHttpService} from '../core/backend-http.service';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {ObjectUtils} from '../utils/object.utils';
import {PlatformSettings} from '../../models/platform-settings/platform-settings';
import {TranslateService} from '@ngx-translate/core';
import {DomSanitizer} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {Subject} from 'rxjs/Subject';
import {InfluencerInfo} from '../../models/influencer';
import {UserBilling} from '../../models/Billing/billing-details';
import {BrandedSignUp} from '../../models/auto-group/branded-sign-up';
import {CacheService} from './cache/cache.service';
import {EventFilter, EventFilterType, EventType, NewEvent} from '../../models/user-notification';
import {retry, tap, map, catchError, filter as rxFilter, distinctUntilChanged, switchMap, pairwise, shareReplay} from 'rxjs/operators';
import { Advertiser, CompanyStatus } from '../../models/common';
import { WebUtils } from '../utils/web.utils';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { of } from 'rxjs/observable/of';

@Injectable()
export class UserService extends BackendHttpService<any> {

  public companyId = new BehaviorSubject(undefined);
  private _platformSettings = new BehaviorSubject(defaultPlatformSettings);
  private _accountLevels = new BehaviorSubject<AccountLevelsUsage>(undefined);
  private allAdvertisers: Advertiser[] = [];
  private _currentInfluencer: InfluencerInfo;
  private _hasStartedTrial = false;
  private _lastViewedCampaignTime;
  private _canViewCampaignNotification = true;
  private _contactSupportSubject = new Subject();
  public userAvatar = CommonConstant.DEFAULT_USER_AVATAR;
  private _isSsoUser: boolean;

  private _activatedInfluencerUpdate = new BehaviorSubject(null);
  public activeInfluencers$;

  private _executeInvalidActionLevel: Subject<any> = new BehaviorSubject<any>(undefined);

  constructor(public router: Router, public translate: TranslateService, private cacheService: CacheService) {
    super(router, translate);
    this.setupActivatedInfluencerUpdateFlow();
  }

  public updatePlatformSettings(platformSettings) {
    this._platformSettings.next(platformSettings);
  }

  public updateAccountLevel(accountLevel) {
    this._accountLevels.next(accountLevel);
  }

  public isFreeUser() {
    const accountLevels = this._accountLevels.getValue();
    return accountLevels && accountLevels.level && accountLevels.level.name === 'Free';
  }

  public isPremiumAccount() {
    const accountLevels = this._accountLevels.getValue();
    return accountLevels && accountLevels.level && accountLevels.level.name === 'Premium';
  }

  public isEnterpriseAccount() {
    const accountLevels = this._accountLevels.getValue();
    return accountLevels && accountLevels.level && accountLevels.level.name === 'Enterprise';
  }

  public accountType() {
    const accountLevels = this._accountLevels.getValue();
    return accountLevels && accountLevels.level ? accountLevels.level.name : null;
  }

  get maxResultPerSearch() {
    const accountLevels = this._accountLevels.getValue();
    if (!accountLevels) {
      return null;
    }

    if (accountLevels.monthlyResultPerSearch) {
      return accountLevels.monthlyResultPerSearch;
    } else {
      return accountLevels.level ? accountLevels.level.maxResultPerSearch : null;
    }
  }

  get maxGroupInfluencers() {
    const accountLevels = this._accountLevels.getValue();
    if (!accountLevels) {
      return null;
    }

    // Todo: consider to remove in error handling
    return null;
  }

  get maxCampaignInfluencers() {
    const accountLevels = this._accountLevels.getValue();
    if (!accountLevels) {
      return null;
    }

    // Todo: consider to remove in error handling
    return null;
  }

  get maxActiveInfluencers() {
    const accountLevels = this._accountLevels.getValue();
    if (!accountLevels) {
      return null;
    }
    if (accountLevels.monthlyActiveInfluencers) {
      return accountLevels.monthlyActiveInfluencers;
    }

    return accountLevels.level.maxActiveInfluencers;
  }

  get maxListeningMentions() {
    const accountLevels = this._accountLevels.getValue();
    if (!accountLevels) {
      return null;
    }

    // Todo: consider to remove in error handling
    return null;
  }

  get currentUserLevel() {
    const accountLevels = this._accountLevels.getValue();
    if (!accountLevels) {
      return null;
    }
    return accountLevels.level;
  }

  get accountLevelName() {
    const currentLevel = this.currentUserLevel;
    return currentLevel ? currentLevel.name : null;
  }

  get accountLimitations() {
    const accountLevels = this._accountLevels.getValue();
    if (!accountLevels) {
      return null;
    }
    return accountLevels.limitations;
  }

  get chartColors() {
    const platformSettings = this._platformSettings.getValue();
    return platformSettings.chartColors;
  }

  get logoImage() {
    if (this.isInfluencer) {
      return defaultPlatformSettings.influencerAppLogoContent;
    }

    const platformSettings = this._platformSettings.getValue();
    return platformSettings && platformSettings.logoContent != null ? platformSettings.logoContent : defaultPlatformSettings.logoContent;
  }

  get leftSidebarLogoImage() {
    const platformSettings = this._platformSettings.getValue();

    if (platformSettings === defaultPlatformSettings) {
      return defaultPlatformSettings.leftSidebarLogoContent;
    }

    return platformSettings && platformSettings.logoContent != null ? platformSettings.logoContent : defaultPlatformSettings.leftSidebarLogoContent;
  }

  get platformName() {
    const platformSettings = this._platformSettings.getValue();
    return platformSettings.platformName;
  }

  get platformBackground() {
    const platformSettings = this._platformSettings.getValue();
    return platformSettings ? platformSettings.platformBgColor : defaultPlatformSettings.platformBgColor;
  }

  get platformSettings(): Observable<PlatformSettings> {
    return this._platformSettings.asObservable();
  }

  get accountLevels(): Observable<AccountLevelsUsage> {
    return this._accountLevels.asObservable();
  }

  public hasCreditBaseBilling() {
    if (!localStorage.getItem('user')) {
      return false;
    }
    const user = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
    return user.hasBilling;
  }

  public get isSsoUser(): boolean {
    if (this._isSsoUser !== undefined) {
      return this._isSsoUser;
    }

    return localStorage.getItem('isSsoUser') === 'true';
  }

  public setIsSsoUser(isSsoUser: boolean) {
    this._isSsoUser = isSsoUser;
    localStorage.setItem('isSsoUser', JSON.stringify(isSsoUser));
  }

  public clearIsSsoUser() {
    this._isSsoUser = undefined;
    localStorage.removeItem('isSsoUser');
  }

  /**
   * change company id when switching account
   * @param {number} id
   */
  public setCompanyId(id: number, byActiveAccountUpdateInUrl?: boolean, leaveInactiveState?: boolean) {
    const activeAccount = this.allAdvertisers.find(adver => adver.id === id);
    // if the active account is not belonged to current agency. Redirect to login page
    if (!activeAccount) {
      this.setFirstAccountAsActive(this.allAdvertisers, null, byActiveAccountUpdateInUrl);
      return;
    }

    this._hasStartedTrial = activeAccount.isTrialed;
    if (this.getCompanyId() === id) {
      return;
    }

    if (activeAccount.inactive) {
      // if user change activeAccount only by updating url, navigate to inactive user page if needed
      // if user change path, inactive account case is handle by auth guard
      if (byActiveAccountUpdateInUrl) {
        WebUtils.navigateToInactivePageIfNeeded(id);
      }

      // just set the company and not call the below apis because of authorization
      this.companyId.next(id);
      return;
    }

    // get out of inactive state if needed
    // ex: admin change account from inactive one to active one
    const getOutOfInactiveState = leaveInactiveState && !activeAccount.inactive;

    // load platform settings for non-SSO users
    if (!this.isSsoUser) {
      this.loadPlatformSettingByCampanyId(id);
    }

    // Influencer companies have no account level configured
    if (!this.isInfluencer) {
      // load account levels of new account
      this.loadAccountLevelsOfCampanyId(id);
    }
    this.getLastViewedTime(EventType.NEW_CAMPAIGN_POST, id).subscribe((res) => {
      this._lastViewedCampaignTime = res;
      this._canViewCampaignNotification = true;
      // update new account id
      this.companyId.next(id);
      if (getOutOfInactiveState) { WebUtils.getOutOfInactiveStateIfNeeded(); }
    }, (err) => {
      this._lastViewedCampaignTime = null;
      if (err.status === 403) {
        this._canViewCampaignNotification = false;
      }
      // update new account id
      this.companyId.next(id);
      if (getOutOfInactiveState) { WebUtils.getOutOfInactiveStateIfNeeded(); }
    });
  }

  /**
   * set company based on current influencer
   * @param {number} id
   */
  public setCurrentCompanyForInfluencer(id: number) {
    this.companyId.next(id);
  }

  public manuallyUpdateActiveAccount(accountId: number) {
    const isAccountExisted = this.allAdvertisers.findIndex(adver => adver.id === accountId) >= 0;
    if (!isAccountExisted) {
      return;
    }
    this.companyId.next(accountId);
  }

  /**
   * Fetch and update platform setting of company base on id
   * @param companyId
   */
  public loadPlatformSettingByCampanyId(companyId) {
    this.getPlatformSettings(companyId).subscribe((setting) => {
        this.updatePlatformSettings(setting);
      }, (error) => {
        this.updatePlatformSettings(defaultPlatformSettings);
      }
    );
  }

  /**
   * Fetch and update platform setting of company base on id
   * @param companyId
   */
  public loadAccountLevelsOfCampanyId(companyId) {
    this.getAccountLevels(companyId).subscribe((levels) => {
        this.updateAccountLevel(levels);
      }
    );
  }

  public getCompanyId() {
    return this.companyId.getValue();
  }

  public resetActiveAccount() {
    this.companyId.next(undefined);
  }

  public setActiveCompany(listAdvertiser, belongingCompany, defaultActiveAccount?: number) {
    if (Array.isArray(listAdvertiser) && listAdvertiser.length > 0) {
      // for admin and agency account
      this.allAdvertisers = listAdvertiser;
    } else {
      // advertiser accounts don’t necessarily have a governing agency. So, use the company which it's belonging to
      this.allAdvertisers = [belongingCompany];
    }

    if (this.getCompanyId()) {
      return;
    }
    this.setFirstAccountAsActive(this.allAdvertisers, defaultActiveAccount);
  }

  private setFirstAccountAsActive(listAdvertises, defaultActiveAccount?: number, byActiveAccountUpdateInUrl?: boolean) {
    if (defaultActiveAccount) {
      this.setCompanyId(defaultActiveAccount, byActiveAccountUpdateInUrl);
    } else if (listAdvertises && listAdvertises[0] && listAdvertises[0].id) {
      if (this.isAgency || this.isAdmin) {
        this.setActiveAccountForAgencyOrAdmin(listAdvertises, byActiveAccountUpdateInUrl);
        return;
      }

      this.setCompanyId(listAdvertises[0].id, byActiveAccountUpdateInUrl);
    }
  }

  private setActiveAccountForAgencyOrAdmin(listAdvertises: Advertiser[], byActiveAccountUpdateInUrl?: boolean) {
    // try to find 1st active company
    const firstActiveCompany = listAdvertises.find(ad => !ad.inactive);

    if (firstActiveCompany) {
      this.setCompanyId(firstActiveCompany.id, byActiveAccountUpdateInUrl);
      return;
    }

    // otherwise use the 1st inactive company
    this.setCompanyId(listAdvertises[0].id, byActiveAccountUpdateInUrl);
  }

  public getAllProjectOfAdvertiser(): Observable<any> {
    const currentUser: User = ObjectUtils.parseJSON(localStorage.getItem('user'));
    if (currentUser === null) {
      return Observable.of([]);
    }
    return this.get(apiEndpoints.getProjectOfAdvertiser + this.getCompanyId());
  }

  public getAllAdvertiser(type, companyId): Observable<any> {
    let endpoint = apiEndpoints.getGoverningAgencyByAgency + companyId;
    if (type === UserType.ADMIN) {
      endpoint = apiEndpoints.getAllAdvertiser;
    }
    return this.get(endpoint).pipe(map(res => {
      const data = res && res.data;

      if (data) {
        data.forEach(x => x.startDate = new Date(x.startDate * 1000));
      }

      return res;
    }));
  }

  public getCurrentUserInfo(): Observable<User> {
    return this.get(apiEndpoints.me).pipe(map(res => {
      const userData = res && res.data;

      if (userData) {
        const company = userData.company;
        company.startDate = new Date(company.startDate * 1000);
      }

      return res;
    }));
  }

  public getCurrentAdvertiser(): Advertiser {
    if (Array.isArray(this.allAdvertisers) && this.allAdvertisers.length > 0) {
      return this.allAdvertisers.find(adv => adv.id === this.getCompanyId());
    }
    return null;
  }

  /**
   * this method use to create new invited user
   * @param {string} hashString
   * @param {UserProfile} user
   * @returns {Observable<any>}
   */
  public acceptionInvitation(hashString: string, user: UserProfile, companyName: string): Observable<any> {
    const splitName = user.fullName.split(' ');

    if (splitName.length >= 2) {
      user.firstName = splitName[0];
      user.lastName = splitName[2] || splitName[1];
    } else {
      user.firstName = splitName[0];
      user.lastName = '';
    }
    user.companyName = companyName;
    user.packageSub = 'PRO';
    return this.post(apiEndpoints.acceptInvitation, {
      hashString, profile: user
    });
  }

  /**
   * this method use to check the invitation hashlink before creating new user
   * @param {string} hashString
   * @param {UserProfile} user
   * @returns {Observable<any>}
   */
  public precheckHashLink(hashString: string): Observable<any> {
    return this.post(apiEndpoints.precheckHashLink, { hashString }).map(res => res.data);
  }

  /**
   * this method use to register an request demo
   * @param {data}
   * @returns {Observable<any>}
   */
  public register(data): Observable<any> {
    return this.post(apiEndpoints.register, data).map(res => res.data);
  }

  public authorizeUserByToken(token: string): Observable<any> {
    return this.get(apiEndpoints.authorizeUserByToken + '/' + token);
  }

  public authorizeUserByInviteToken(token: string, campaignId: string): Observable<any> {
    return this.get(apiEndpoints.authorizeUserByInviteToken, {
      inviteToken: token,
      campaignId
    });
  }

  public savePlatformSettings(companyId: number, platformSettings: PlatformSettings): Observable<any> {
    return this.put(apiEndpoints.platformSettings.replace(':companyId', companyId.toString()), platformSettings);
  }

  public getPlatformSettings(companyId: number): Observable<any> {
    return this.get(apiEndpoints.platformSettings.replace(':companyId', companyId.toString())).map(res => res.data);
  }

  public getAccountLevels(companyId: number): Observable<any> {
    return this.get(apiEndpoints.accountLevels.replace(':companyId', companyId.toString()))
      .pipe(
        map(res => res.data),
        catchError(error => {
          WebUtils.reportErrorToSentry('Get Account Levels error', error);
          return Observable.throw(error);
        })
      );
  }

  public getCompanyName(companyId) {
    const company = this.allAdvertisers.find(adv => adv.id === companyId);
    if (!company) {
      return null;
    }

    return company.name;
  }

  public getAdvertiserList() {
    return this.allAdvertisers.map(adv => ({
      id: adv.id,
      name: adv.name,
      status: adv.status,
      inactive: adv.inactive
    }));
  }

  public doInvalidActionByLevel(actionName, errorMsg?: string) {
    this._executeInvalidActionLevel.next({
      actionName,
      message: errorMsg ? errorMsg : this.translate.instant('common.invalid_action_by_levels_warning.common_msg')
    });
  }

  get invalidActionLevelObject(): Observable<any> {
    return this._executeInvalidActionLevel.asObservable();
  }

  public resetInvalidActionObject() {
    this._executeInvalidActionLevel.next(undefined);
  }

  public signUp(newUser: NewUser): Observable<any> {
    if (this.isExistedUTMData()) {
      newUser = Object.assign({}, newUser, {
        utm_source: localStorage.getItem('utm_source'),
        utm_campaign: localStorage.getItem('utm_campaign'),
        utm_medium: localStorage.getItem('utm_medium')
      });
    }
    return this.post(apiEndpoints.signUp, newUser);
  }

  private isExistedUTMData() {
    return localStorage.getItem('utm_source') || localStorage.getItem('utm_campaign') || localStorage.getItem('utm_medium');
  }

  public resetPassword(data: any): Observable<any> {
    return this.post(apiEndpoints.resetPasswordHash, data).map(res => res.data);
  }

  public verifyEmail(token: String): Observable<any> {
    return this.get(apiEndpoints.emailVerification, { token });
  }

  get isAdmin() {
    const user = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
    return user && user.type === UserType.ADMIN;
  }

  get isAgency() {
    const user = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
    return user && user.type === UserType.AGENCY;
  }

  get isAdvertiser() {
    const user = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
    return user && user.type === UserType.ADVERTISER;
  }

  public loadInfluencerInfoByCompanyId(companyId): Observable<InfluencerInfo> {
    if (!companyId) {
      return Observable.of(null);
    }
    return this.get(apiEndpoints.getInfluencerInfo.replace(':companyId', companyId)).map((res) =>
      res && res.data ? Object.assign({}, res.data, {
        profilePicture: res.data.profile_image,
    }) : null);
  }

  public addInfluencerInfo(influencerData: InfluencerInfo) {
    this._currentInfluencer = influencerData;
  }

  public getInfluencerInfo() {
    return this._currentInfluencer;
  }

  get isInfluencer() {
    const user = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
    return user && user.type === UserType.INFLUENCER;
  }

  get isAnalyst() {
    const user = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
    return user && user.type === UserType.ANALYST;
  }

  acceptionTermAndCondition(userId, title): Observable<any> {
    return this.post(apiEndpoints.updateUserProfile, {
      userId,
      title
    });
  }

  public updateUser(firstName: string, lastName: string, email: string, image: string): Observable<any> {
    const currentUser: User = ObjectUtils.parseJSON(localStorage.getItem('user'));
    return this.post(apiEndpoints.updateUserProfile, {
      userId: currentUser.data.id,
      firstName, lastName, email, image
    });
  }

  public updateAcceptTermAndService(isAcceptedTermAndService: boolean) {
    this._currentInfluencer.acceptedTermService = isAcceptedTermAndService;
  }

  public updatePassword(oldPassword, newPassword): Observable<any> {
    return this.post(apiEndpoints.changepassword, {
      password: oldPassword,
      newPassword: newPassword
    });
  }

  public hasStartedTrial() {
    return this._hasStartedTrial;
  }

  public loadAccountListByUser(user: User, callback?) {
    const task = this.loadAccountListByUserTask(user);

    task.subscribe({
      next: () => {
        if (callback) {
          callback();
        }
      }
    });
  }

  public loadAccountListByUserTask(user: User, defaultActiveAccount?: number): Observable<any> {
    const belongingCompany = {
      id: user.companyId,
      name: user.companyName
    } as Partial<Advertiser>;

    const task = user.type === UserType.ADVERTISER
      ? this.getCurrentUserInfo()
        .pipe(tap((res: any) => {
          const userData = res && res.data;
          if (userData) {
            belongingCompany.status = userData.company.customerStatus;
            belongingCompany.inactive = this.isAdvertiserInactive(belongingCompany);
            belongingCompany.startDate = userData.company.startDate;
            this.setActiveCompany([], belongingCompany, defaultActiveAccount);
          }
        }))
      : this.getAllAdvertiser(user.type, user.companyId)
        .pipe(tap((res: any) => {
          if (res && res.data) {
            const advertisers = res.data as Partial<Advertiser>[];
            advertisers.forEach(ad => ad.inactive = this.isAdvertiserInactive(ad));
            this.setActiveCompany(advertisers, belongingCompany, defaultActiveAccount);
          }
        }));

    return task;
  }

  private isAdvertiserInactive(advertiser: Partial<Advertiser>): boolean {
    return advertiser.status === CompanyStatus.BLOCKED
      || advertiser.status === CompanyStatus.CHURNED
      || advertiser.status === CompanyStatus.FUTURE;
  }

  public loadReviewerList(companyId: number) {
    return this.get(apiEndpoints.getReviewerList.replace(':companyId', companyId.toString()))
      .map(res => res && res.data ? res.data : []);
  }

  public getUserBilling(companyId: number): Observable<UserBilling> {
    return this.get(apiEndpoints.userBilling.replace(':companyId', companyId.toString()))
      .map(res => res && res.data ? res.data : null);
  }

  public getUserMonthlyUsage(companyId: number): Observable<any> {
    return this.get(apiEndpoints.userMonthlyUsage.replace(':companyId', companyId.toString()));
  }

  public updateProfile(userProfileData) {
    return this.post(apiEndpoints.updateProfile, userProfileData);
  }

  public updateCurrentUserAvatar(image, user: User) {
    user.data.image = image;
    localStorage.setItem('user', JSON.stringify(user));
  }

  get getStartedTrialValue() {
    if (Array.isArray(this.allAdvertisers) && this.allAdvertisers.length > 0) {
      const currentActiveAccount = this.allAdvertisers.find(adv => adv.id === this.getCompanyId());
      if (!currentActiveAccount) {
        return false;
      }
      return currentActiveAccount.isTrialed || !this.isFreeUser();
    }
    return false;
  }

  public getSignUpPageData(companyId): Observable<BrandedSignUp> {
    return this.get(apiEndpoints.getSignUpPageData.replace(':companyId', companyId.toString()))
      .map(res => res && res.data ? res.data : null);
  }

  public loadNotificationSetting(): Observable<NotificationSetting> {
    return this.get(apiEndpoints.notificationSetting).map((res) => {
      if (!res || !res.data) {
        return null;
      }
      return this.normalizeNotificationSettings(res);
    });
  }

  public getUserNotificationSettingByToken(userId, token): Observable<NotificationSetting> {
    return this.get(apiEndpoints.emailSettings, {
      user_id: userId,
      token
    }).map((res) => {
      if (!res || !res.data) {
        return null;
      }
      return this.normalizeNotificationSettings(res);
    });
  }

  public saveUserNotificationSettingByToken(userId, token, settingData: NotificationSetting): Observable<NotificationSetting> {
    const settings = [
      {
        type: CommonConstant.NOTIFICATION_ACTIVITY_SETTING,
        schedule: settingData.activity
      },
      {
        type: CommonConstant.NOTIFICATION_UPDATES_SETTING,
        schedule: settingData.updates
      },
      {
        type: CommonConstant.NOTIFICATION_PENDING_ACTION_SETTING,
        schedule: settingData.pendingAction
      },
      {
        type: CommonConstant.NOTIFICATION_PROMOTION_EMAIL_SETTING,
        schedule: settingData.promotionalEmails
      }
    ];
    return this.post(`${apiEndpoints.emailSettings}?user_id=${userId}&token=${token}`, {
      settings
    }).map((res) => res ? res.data : null);
  }

  private normalizeNotificationSettings(res) {
    const notificationSetting = new NotificationSetting();
    const activity = res.data.settings.find(noti => noti.type === CommonConstant.NOTIFICATION_ACTIVITY_SETTING);
    if (activity) {
      notificationSetting.activity = activity.schedule;
    }
    const updates = res.data.settings.find(noti => noti.type === CommonConstant.NOTIFICATION_UPDATES_SETTING);
    if (updates) {
      notificationSetting.updates = updates.schedule;
    }
    const pendingAction = res.data.settings.find(noti => noti.type === CommonConstant.NOTIFICATION_PENDING_ACTION_SETTING);
    if (pendingAction) {
      notificationSetting.pendingAction = pendingAction.schedule;
    }
    const promotionalEmail = res.data.settings.find(noti => noti.type === CommonConstant.NOTIFICATION_PROMOTION_EMAIL_SETTING);
    if (promotionalEmail) {
      notificationSetting.promotionalEmails = promotionalEmail.schedule;
    }
    return notificationSetting;
  }

  public saveNotificationSetting(notificationSetting: NotificationSetting): Observable<any> {
    const data = [
      {
        type: CommonConstant.NOTIFICATION_ACTIVITY_SETTING,
        schedule: notificationSetting.activity
      },
      {
        type: CommonConstant.NOTIFICATION_UPDATES_SETTING,
        schedule: notificationSetting.updates
      },
      {
        type: CommonConstant.NOTIFICATION_PENDING_ACTION_SETTING,
        schedule: notificationSetting.pendingAction
      }
    ];
    return this.post(apiEndpoints.notificationSetting, {
      settings: data
    });
  }

  public getSampleData() {
    return this.get(apiEndpoints.sampleSetups).map(res => res && res.data ? res.data : []);
  }

  public createSampleData(sampleSetUpId: number, companyId: number) {
    return this.post(apiEndpoints.setupSampleData.replace(':companyId', companyId.toString()), {
      sampleSetupId: sampleSetUpId
    }).map(res => res);
  }

  public clearAdvertiserData() {
    this.allAdvertisers = [];
    this.cacheService.clear(CommonConstant.LIST_ADVERTISER_BELONG_TO_USER);
  }

  /**
   * get the last view from session storage if persisted. Otherwise call API to get from server with retry on error
   * @param {EventType} eventType
   * @param {number} companyId
   */
  public getLastViewedTime(eventType: EventType, companyId: number): Observable<number> {
    const user = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
    if (!user) {
      return Observable.throw(new Error('Not found user'));
    }
    const lvcKey = `${CommonConstant.LAST_VIEWED_CAMPAIGN_TIME_KEY_PREFIX}_${user.data.id}_${companyId}`;
    if (!sessionStorage.getItem(lvcKey)) {
      const apiUrl = apiEndpoints.lastViewedTime.replace(':type', eventType.toString()).replace(':company_id', companyId.toString());
      return this.get(apiUrl).pipe(retry(CommonConstant.MAX_RETRY_ON_ERROR_LAST_VIEW_TIME))
        .map((res) => {
          if (res && res.data && res.data.last_viewed_at !== null) {
            sessionStorage.setItem(lvcKey, res.data.last_viewed_at);
            return res.data.last_viewed_at;
          }
          return null;
        });
    } else {
      return Observable.of(Number(sessionStorage.getItem(lvcKey)));
    }
  }

  /**
   * Call API to update the last view time on campaign pages. Retry on error
   * @param {EventType} eventType
   * @param {number} companyId
   * @returns {Observable<any>}
   */
  public updateLastViewTime(eventType: EventType, companyId: number) {
    if (!this._canViewCampaignNotification) {
      return;
    }
    const apiUrl = apiEndpoints.lastViewedTime.replace(':type', eventType.toString()).replace(':company_id', companyId.toString());
    this.put(apiUrl, {}).pipe(retry(CommonConstant.MAX_RETRY_ON_ERROR_LAST_VIEW_TIME)).subscribe();
  }

  public lastViewedCampaignTime() {
    return this._lastViewedCampaignTime;
  }

  /**
   * load the new events based on the event type from a specific timestamp.
   * Allow to filter with format key=${campaign_id|influencer_id|url_hash}&filter=${campaign_id|influencer_id|campaign_influencer_id}:${value}
   * @param {EventType} eventType
   * @param {number} companyId
   * @param {number} timestamp
   * @param {EventFilter} filter
   * @returns {Observable<any>}
   */
  public getNewEventFromLastTime(eventType: EventType, companyId: number, timestamp: number, filter: EventFilter): Observable<NewEvent> {
    if (!eventType || !companyId || !timestamp || !filter) {
      return Observable.of(null);
    }
    const apiUrl = apiEndpoints.getNewEventsFromTimestamp.replace(':type', eventType.toString())
      .replace(':company_id', companyId.toString())
      .replace(':timestamp', timestamp.toString());
    const requestParams = this.buildRequestParams(filter);
    return this.get(apiUrl, requestParams).map(res => res ? res.data : null).catch((err) => {
      if (err.status < 500) {
        return Observable.of(null);
      }
      return Observable.throw(err);
    });
  }

  private buildRequestParams(filter: EventFilter) {
    const requestParams = {};
    switch (filter.type)  {
      case EventFilterType.CAMPAIGN_ID:
        return Object.assign({}, requestParams, {
          key: filter.type
        });
      case EventFilterType.INFLUENCER_ID:
        return Object.assign({}, requestParams, {
          key: filter.type,
          filter: `campaign_id:${filter.value}`
        });
      case EventFilterType.URL_HASH:
        return Object.assign({}, requestParams, {
          key: filter.type,
          filter: `campaign_influencer_id:${filter.value}`
        });
      default:
        throw new Error('Unsupport event type: ' + filter.type);
    }
  }

  public clearCampaignLastViewTime() {
    this._lastViewedCampaignTime = null;
    this._canViewCampaignNotification = true;
  }

  public generateCheckoutNewSubscription(companyId) {
    return this.get(apiEndpoints.checkoutSubscription.replace(':companyId', companyId.toString())).map(res => res.data);
  }

  public getPortalSession(companyId) {
    return this.get(apiEndpoints.portalSession.replace(':companyId', companyId.toString())).map(res => res.data);
  }

  public contactSupport(): void {
    this._contactSupportSubject.next();
  }

  public get contactSupportRequested(): Observable<any> {
    return this._contactSupportSubject.asObservable();
  }

  private getInfluencerUsage(companyId) {
    return this.get(apiEndpoints.influencerUsage.replace(':companyId', companyId.toString())).map(res => res.data);
  }

  public triggerUpdateActivatedInfluencers() {
    this._activatedInfluencerUpdate.next(null);
  }

  private setupActivatedInfluencerUpdateFlow() {
    this.activeInfluencers$ = combineLatest([
      this.companyId.asObservable().pipe(rxFilter(x => !!x), distinctUntilChanged()),
      this._activatedInfluencerUpdate.asObservable()
    ]).pipe(
      switchMap(([companyId]) => {
        return this.getInfluencerUsage(companyId).pipe(catchError(() => of(null)));
      }),
      shareReplay()
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/services/user.service.ts